import { UUID } from "../outings";

export const FREESTYLE_MAX_SOURCE_BYTES = 128 * 1024 * 1024;
export const FREESTYLE_UPLOAD_CHUNK_BYTES = 6 * 1024 * 1024;
export const FREESTYLE_SOURCE_BUCKET = "companion-freestyle-sources";
export const FREESTYLE_SOURCE_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const;
export type FreestyleSourceType = typeof FREESTYLE_SOURCE_TYPES[number];
export type FreestyleUploadGrant = {
  owner: string; clipId: string; path: string; sha256: string; bytes: number;
  contentType: FreestyleSourceType; token: string; expiresAt: string;
};
export type FreestyleUploadRecovery = {
  owner: string; clipId: string; path: string; sha256: string; bytes: number; url: string;
};

export function freestyleSourcePath(owner: string, clipId: string, sha256: string): string {
  if (!UUID.test(owner) || !UUID.test(clipId) || !/^[a-f0-9]{64}$/.test(sha256)) throw new Error("Reopen your clip before uploading.");
  return `${owner}/${clipId}/${sha256}.source`;
}

export function validFreestyleUploadGrant(grant: FreestyleUploadGrant, now = Date.now()): boolean {
  try {
    return grant.path === freestyleSourcePath(grant.owner, grant.clipId, grant.sha256) &&
      Number.isSafeInteger(grant.bytes) && grant.bytes > 0 && grant.bytes <= FREESTYLE_MAX_SOURCE_BYTES &&
      FREESTYLE_SOURCE_TYPES.includes(grant.contentType) &&
      typeof grant.token === "string" && grant.token.length >= 20 && grant.token.length <= 8192 && !/[\s\x00-\x1f\x7f]/.test(grant.token) &&
      Number.isFinite(Date.parse(grant.expiresAt)) && Date.parse(grant.expiresAt) > now && Date.parse(grant.expiresAt) <= now + 2 * 60 * 60_000;
  } catch { return false; }
}

/** Only the configured Supabase project can receive the upload capability. */
export function freestyleUploadEndpoint(configuredProjectUrl: string): string {
  const url = new URL(configuredProjectUrl);
  if (url.protocol !== "https:" || !/^[a-z0-9]{1,80}\.supabase\.co$/.test(url.hostname) || url.username || url.password || url.port || url.search || url.hash || url.pathname !== "/")
    throw new Error("Private uploads are unavailable.");
  return `https://${url.hostname.replace(".supabase.co", ".storage.supabase.co")}/storage/v1/upload/resumable/sign`;
}

export function validFreestyleUploadUrl(value: string, endpoint: string): boolean {
  try {
    const target = new URL(value), base = new URL(endpoint);
    return target.protocol === "https:" && target.origin === base.origin && !target.username && !target.password && !target.search && !target.hash &&
      target.pathname.startsWith(`${base.pathname}/`) && target.pathname.length > base.pathname.length + 1 && value.length < 2048;
  } catch { return false; }
}
